import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Button, Input } from '@nsibi/ui-web';
import { Trans, useTranslation } from 'react-i18next';

import {
  DEFAULT_RESEND_SECONDS,
  RegistrationLockError,
  startEmail,
  verifyEmail,
} from '../lib/auth';
import {
  EMAIL_MAX,
  cleanEmail,
  emailProblem,
  maskEmail,
  readEmailFailure,
  waitParts,
} from '../lib/emailAuth';
import { currentLocale } from '../lib/i18n';
import type { Session } from '../lib/session';
import { useCountdown } from '../lib/useCountdown';
import { AgeRefused } from './AgeRefused';
import { PinStep } from './PinStep';

/** The two documents somebody agrees to by continuing. The trailing slash
 *  matters: the site sets `trailingSlash: true`, so the bare path redirects. */
const LEGAL = { terms: 'https://nsibi.app/terms/', privacy: 'https://nsibi.app/privacy/' };

/** A six-digit emailed code (`auth_phone/utils.py::generate_otp_code(6)`). */
const CODE_LENGTH = 6;

type Step = 'email' | 'code' | 'pin' | 'refused';

type Lock = { challenge: string; retryAfter: number };

/**
 * Sign in with an emailed code.
 *
 * It is the phone flow with the number swapped for an address: an entry step,
 * the SAME code step, and - for an account with two-step verification - the
 * registration-lock PIN. A new account goes on to the profile step
 * (`ProfileSetup`), which the app shows while the stored session says a name or
 * a date of birth is still owed.
 *
 * What it owes the person, in the order it hurts to get wrong:
 *
 *  1. A refusal that says why. The server answers a start the same way
 *     whether or not an account exists (it must not become a way to find out
 *     who has one), so the failures that CAN be told are the operational
 *     ones - mail is down, too many attempts, the feature is off - and each
 *     gets its own sentence and, where there is a wait, a countdown.
 *  2. A code already out is not an error. Asking again inside the wait carries
 *     the person to the code step with the server's own countdown.
 *  3. A wrong PIN is not a wrong code. It never sends anybody back for another
 *     email; the code is already spent and the PIN has its own ladder.
 */
export function EmailSignIn({
  onSignedIn,
  onUseScan,
  onSwitchOff,
}: {
  onSignedIn: (s: Session) => void;
  /** Back to the QR door. */
  onUseScan: () => void;
  /** The server answered that email sign-in is off: re-read the methods. */
  onSwitchOff?: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('email');
  const [raw, setRaw] = useState('');
  const [code, setCode] = useState('');
  const [lock, setLock] = useState<Lock | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Two independent waits: the entry step's throttle and the gap before
  // another code may be asked for. The PIN keeps its own (`PinStep`).
  const entryWait = useCountdown();
  const resend = useCountdown();

  const email = cleanEmail(raw);
  const emailOk = emailProblem(raw) === null;

  const waitText = useCallback(
    (seconds: number) => {
      const { unit, n } = waitParts(seconds);
      return unit === 'h'
        ? t('auth.email.tryAgainInH', { n, defaultValue: 'Try again in {{n}} h' })
        : unit === 'min'
          ? t('auth.email.tryAgainInMin', { n, defaultValue: 'Try again in {{n}} min' })
          : t('auth.email.tryAgainInS', { n, defaultValue: 'Try again in {{n}}s' });
    },
    [t],
  );

  const resendText = useCallback(
    (seconds: number) => {
      const { unit, n } = waitParts(seconds);
      return unit === 'h'
        ? t('auth.resendInH', { hours: n, defaultValue: 'Resend in {{hours}} h' })
        : unit === 'min'
          ? t('auth.resendInMin', { minutes: n, defaultValue: 'Resend in {{minutes}} min' })
          : t('auth.resendInS', { cooldown: n, defaultValue: 'Resend in {{cooldown}}s' });
    },
    [t],
  );

  const tooMany = useCallback(
    () =>
      t('auth.email.tooMany', {
        defaultValue: 'Too many attempts. Wait a little before trying again.',
      }),
    [t],
  );
  const offText = useCallback(
    () =>
      t('auth.email.off', {
        defaultValue: "Email sign-in isn't available right now. Use your phone number instead.",
      }),
    [t],
  );
  const networkText = useCallback(
    () =>
      t('auth.email.network', {
        defaultValue: "Can't reach the server. Check your connection and try again.",
      }),
    [t],
  );
  const appCheckText = useCallback(
    () =>
      t('auth.web.appCheckFailed', {
        defaultValue: "This browser can't sign in right now. Use the Nsibi app, or try again later.",
      }),
    [t],
  );

  // ── the entry step ────────────────────────────────────────────────

  /** Ask for a code. `from` is the step that asked: a refusal means something
   *  different on the entry form than behind the Resend button. */
  const sendCode = useCallback(
    async (from: 'email' | 'code') => {
      setBusy(true);
      setError(null);
      try {
        const res = await startEmail(email, currentLocale());
        resend.start(res.resendAfterSeconds);
        setCode('');
        setNotice(null);
        setStep('code');
      } catch (err) {
        const failure = readEmailFailure(err);
        switch (failure.kind) {
          case 'resend_too_soon':
            // A code is already out. On the entry form that is where to go;
            // behind Resend it only means the wait was longer than we thought.
            resend.start(failure.retryAfter || DEFAULT_RESEND_SECONDS);
            if (from === 'email') {
              setNotice(
                failure.codeExpired
                  ? t('auth.earlierCodeExpired', {
                      defaultValue:
                        'The code we sent before has expired. Ask for a new one when the timer runs out.',
                    })
                  : null,
              );
              setCode('');
              setStep('code');
            }
            break;
          case 'throttled':
          case 'signup_limited':
            entryWait.start(failure.retryAfter || 60);
            if (from === 'code') resend.start(failure.retryAfter || 60);
            setError(tooMany());
            break;
          case 'unavailable':
            setError(
              t('auth.email.unavailable', {
                defaultValue: "We couldn't send the email right now. Try again in a moment.",
              }),
            );
            break;
          case 'off':
            onSwitchOff?.();
            setError(offText());
            break;
          case 'network':
            setError(networkText());
            break;
          case 'app_check':
            setError(appCheckText());
            break;
          case 'detail':
            // The server's own sentence is English; a 400 here is the one case
            // it is worth translating, because it is the one a person can fix.
            setError(
              failure.status === 400
                ? t('auth.email.invalid', {
                    defaultValue: "That email doesn't look right. Check it and try again.",
                  })
                : t('auth.email.sendFailed', { defaultValue: "Couldn't send the code. Try again." }),
            );
            break;
          default:
            setError(
              t('auth.email.sendFailed', { defaultValue: "Couldn't send the code. Try again." }),
            );
        }
      } finally {
        setBusy(false);
      }
    },
    [appCheckText, email, entryWait, networkText, offText, onSwitchOff, resend, t, tooMany],
  );

  function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (busy || entryWait.left > 0) return;
    if (!emailOk) {
      setError(
        t('auth.email.invalid', {
          defaultValue: "That email doesn't look right. Check it and try again.",
        }),
      );
      return;
    }
    void sendCode('email');
  }

  // ── the code step ─────────────────────────────────────────────────

  const verify = useCallback(
    async (value: string) => {
      setBusy(true);
      setError(null);
      try {
        const { session } = await verifyEmail(email, value);
        onSignedIn(session);
      } catch (err) {
        // "You need your PIN" is a step, not a failure.
        if (err instanceof RegistrationLockError && err.challenge) {
          setLock({ challenge: err.challenge, retryAfter: err.retryAfterSeconds });
          setNotice(null);
          setCode('');
          setStep('pin');
          return;
        }
        const failure = readEmailFailure(err);
        switch (failure.kind) {
          case 'age_refused':
            setStep('refused');
            break;
          case 'signup_closed':
            setError(
              t('auth.email.signupClosed', {
                defaultValue: "Creating an account with an email isn't available yet.",
              }),
            );
            break;
          case 'signup_limited':
          case 'throttled':
            setError(tooMany());
            break;
          case 'off':
            onSwitchOff?.();
            setError(offText());
            break;
          case 'network':
            setError(networkText());
            break;
          case 'app_check':
            setError(appCheckText());
            break;
          default:
            // A wrong, expired or spent code. The server's sentence is English
            // and says no more than this does.
            setError(t('auth.thatCodeDidnTWork', { defaultValue: "That code didn't work." }));
        }
        setCode('');
      } finally {
        setBusy(false);
      }
    },
    [appCheckText, email, networkText, offText, onSignedIn, onSwitchOff, t, tooMany],
  );

  // Verify the moment the field is full, so nobody has to find a button after
  // pasting a code. Re-arms on its own: a failure clears the field.
  useEffect(() => {
    if (step === 'code' && code.length === CODE_LENGTH && !busy) void verify(code);
    // `verify` is deliberately not a dependency: it changes with `t` and the
    // wait hooks, and re-running this on those would re-submit a spent code.
  }, [code, step]);

  function submitCode(e: FormEvent) {
    e.preventDefault();
    if (busy || code.length !== CODE_LENGTH) return;
    void verify(code);
  }

  function changeEmail() {
    setStep('email');
    setCode('');
    setError(null);
    setNotice(null);
  }

  // ── drawing ───────────────────────────────────────────────────────

  if (step === 'refused') return <AgeRefused />;

  if (step === 'pin' && lock) {
    return (
      <PinStep
        identity={{ email }}
        challenge={lock.challenge}
        retryAfter={lock.retryAfter}
        onSignedIn={onSignedIn}
        // The challenge belongs to this address and its spent code, so
        // leaving the step drops both.
        onBack={() => {
          setLock(null);
          changeEmail();
        }}
        onExpired={() => {
          // The code behind the challenge is gone: the only way on is a new one.
          setLock(null);
          setStep('email');
          setError(t('auth.web.stepExpired', { defaultValue: 'That step expired. Start again.' }));
        }}
        backLabel={t('auth.web.useDifferentEmail', { defaultValue: 'Use a different email' })}
      />
    );
  }

  if (step === 'code') {
    return (
      <form onSubmit={submitCode}>
        <h1 className="text-display">{t('components.enterYourCode')}</h1>
        <p className="mt-3 text-muted-foreground">
          {/* <Trans>, not two `t()` calls either side of the span: where the
              address sits in the sentence is a property of the LANGUAGE. */}
          <Trans
            i18nKey="auth.email.sentTo"
            values={{ email: maskEmail(email) }}
            components={{ address: <span className="font-medium text-foreground" /> }}
            defaults="We sent a code to <address>{{email}}</address>"
          />
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('auth.email.checkInbox', { defaultValue: 'Check your inbox - and your spam folder.' })}
        </p>

        {notice && (
          <p role="status" className="mt-4 text-sm text-muted-foreground">
            {notice}
          </p>
        )}

        <label htmlFor="code" className="mt-8 block text-sm font-medium">
          {t('components.verificationCode')}
        </label>
        <Input
          id="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="123456"
          maxLength={CODE_LENGTH}
          value={code}
          readOnly={busy}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH));
            if (error) setError(null);
          }}
          className="mt-2 h-11 font-mono tracking-[0.3em]"
        />

        {error && (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {error}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          disabled={busy || code.length !== CODE_LENGTH}
          className="mt-6 w-full"
        >
          {busy ? <Loader2 className="animate-spin" /> : null}
          {busy ? t('components.checking') : t('components.signIn')}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-3 w-full"
          disabled={busy || resend.left > 0}
          onClick={() => void sendCode('code')}
        >
          {resend.left > 0
            ? resendText(resend.left)
            : t('auth.resendCode', { defaultValue: 'Resend code' })}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full"
          disabled={busy}
          onClick={changeEmail}
        >
          <ArrowLeft />
          {t('auth.web.useDifferentEmail', { defaultValue: 'Use a different email' })}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={submitEmail} noValidate>
      <h1 className="text-display">
        {t('auth.email.title', { defaultValue: "What's your email?" })}
      </h1>
      <p className="mt-3 text-muted-foreground">
        {t('auth.email.subtitle', { defaultValue: "We'll send you a six-digit code to sign in." })}
      </p>

      <label htmlFor="email" className="mt-8 block text-sm font-medium">
        {t('auth.email.placeholder', { defaultValue: 'Email address' })}
      </label>
      <Input
        id="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoFocus
        maxLength={EMAIL_MAX}
        placeholder="name@example.com"
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value);
          if (error) setError(null);
        }}
        aria-invalid={error ? true : undefined}
        className="mt-2 h-11"
      />

      {error && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      )}

      <Button
        type="submit"
        size="lg"
        disabled={busy || !emailOk || entryWait.left > 0}
        className="mt-6 w-full"
      >
        {busy ? <Loader2 className="animate-spin" /> : null}
        {busy
          ? t('components.sending')
          : entryWait.left > 0
            ? waitText(entryWait.left)
            : t('components.sendCode')}
        {!busy && entryWait.left === 0 && <ArrowRight />}
      </Button>

      <Button type="button" variant="ghost" size="sm" className="mt-3 w-full" onClick={onUseScan}>
        {t('routes.scanACodeInstead')}
      </Button>

      <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground">
        {/* Consent at the point of consent: an account can be created from here. */}
        <Trans
          i18nKey="auth.legalConsent"
          components={{
            terms: (
              <a
                href={LEGAL.terms}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2 hover:text-foreground"
              />
            ),
            privacy: (
              <a
                href={LEGAL.privacy}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2 hover:text-foreground"
              />
            ),
          }}
          defaults="By continuing you agree to our <terms>Terms of Service</terms> and <privacy>Privacy Policy</privacy>."
        />
      </p>
    </form>
  );
}
